import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import BackToTop from './BackToTop';
import SEO from './SEO';
import { useTheme } from '../context/ThemeContext';

// ==================== SMOOTH SCROLL SYSTEM ====================
const useSmoothScroll = () => {
  const [scrollY, setScrollY] = useState(0);
  const [smoothScrollY, setSmoothScrollY] = useState(0);
  const [velocity, setVelocity] = useState(0);
  const rafRef = useRef(null);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const lerp = (start, end, factor) => start + (end - start) * factor;
    
    const animate = () => {
      const currentScroll = window.scrollY;
      setScrollY(currentScroll);
      
      // Calculate velocity for momentum effects
      const newVelocity = currentScroll - lastScrollY.current;
      setVelocity(lerp(velocity, newVelocity, 0.1));
      lastScrollY.current = currentScroll;
      
      // Smooth interpolation
      setSmoothScrollY(prev => lerp(prev, currentScroll, 0.08));
      
      rafRef.current = requestAnimationFrame(animate);
    };
    
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [velocity]);

  return { scrollY, smoothScrollY, velocity };
};

// ==================== MOUSE TRACKER ====================
const useMouseTracker = () => {
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5, velocity: { x: 0, y: 0 } });
  const lastMouse = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      const vx = x - lastMouse.current.x;
      const vy = y - lastMouse.current.y;
      lastMouse.current = { x, y };
      setMouse({ x, y, velocity: { x: vx * 10, y: vy * 10 } });
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return mouse;
};

// ==================== MAGNETIC ELEMENT ====================
const MagneticElement = ({ children, strength = 30, className = '' }) => {
  const ref = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const distX = (e.clientX - centerX) / rect.width;
    const distY = (e.clientY - centerY) / rect.height;
    setOffset({ x: distX * strength, y: distY * strength });
  }, [strength]);

  const handleMouseLeave = useCallback(() => {
    setOffset({ x: 0, y: 0 });
  }, []);

  return (
    <div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={className}
      style={{
        transform: `translate(${offset.x}px, ${offset.y}px)`,
        transition: 'transform 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
      }}
    >
      {children}
    </div>
  );
};

// ==================== INTERCONNECTED PARTICLES ====================
const InterconnectedParticles = ({ count = 30, color = '#F6CD17', connectDistance = 150 }) => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Initialize particles
    particlesRef.current = Array.from({ length: count }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      radius: Math.random() * 2 + 1,
    }));

    const handleMouse = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', handleMouse);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const particles = particlesRef.current;
      const mouse = mouseRef.current;

      particles.forEach((p, i) => {
        // Mouse attraction
        const dx = mouse.x - p.x;
        const dy = mouse.y - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 200) {
          p.vx += dx * 0.00005;
          p.vy += dy * 0.00005;
        }

        // Update position
        p.x += p.vx;
        p.y += p.vy;

        // Boundary wrap
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        // Draw particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = color + '40';
        ctx.fill();

        // Connect particles
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < connectDistance) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = color + Math.floor((1 - dist / connectDistance) * 30).toString(16).padStart(2, '0');
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      });

      rafRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouse);
    };
  }, [count, color, connectDistance]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ opacity: 0.6 }}
    />
  );
};

// ==================== PARALLAX SHAPE ====================
const ParallaxShape = ({ 
  type, 
  size, 
  color, 
  x, 
  y, 
  scrollY, 
  smoothScrollY,
  mouse,
  parallaxSpeed = 0.1,
  rotationSpeed = 0,
  mouseInfluence = 20,
  delay = 0
}) => {
  const baseTranslateY = (smoothScrollY - delay) * parallaxSpeed;
  const mouseOffsetX = (mouse.x - 0.5) * mouseInfluence;
  const mouseOffsetY = (mouse.y - 0.5) * mouseInfluence;
  const rotation = scrollY * rotationSpeed;
  
  const style = {
    position: 'absolute',
    left: `${x}%`,
    top: `${y}%`,
    transform: `
      translate(${mouseOffsetX}px, ${baseTranslateY + mouseOffsetY}px)
      rotate(${rotation}deg)
    `,
    transition: 'transform 0.1s linear',
    width: size,
    height: size,
  };

  const shapes = {
    circle: (
      <div style={style}>
        <div className="w-full h-full rounded-full border-2" style={{ borderColor: color }} />
      </div>
    ),
    'circle-filled': (
      <div style={style}>
        <div className="w-full h-full rounded-full" style={{ backgroundColor: `${color}20` }} />
      </div>
    ),
    ring: (
      <div style={style} className="relative">
        {[1, 0.75, 0.5, 0.25].map((scale, i) => (
          <div
            key={i}
            className="absolute rounded-full border"
            style={{
              width: `${scale * 100}%`,
              height: `${scale * 100}%`,
              top: `${(1 - scale) * 50}%`,
              left: `${(1 - scale) * 50}%`,
              borderColor: `${color}${Math.round((1 - i * 0.2) * 80).toString(16).padStart(2, '0')}`,
            }}
          />
        ))}
      </div>
    ),
    hexagon: (
      <div style={style}>
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <polygon 
            points="50,5 95,27.5 95,72.5 50,95 5,72.5 5,27.5" 
            fill="none" 
            stroke={color} 
            strokeWidth="2"
          />
        </svg>
      </div>
    ),
    triangle: (
      <div style={style}>
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <polygon 
            points="50,10 90,90 10,90" 
            fill="none" 
            stroke={color} 
            strokeWidth="2"
          />
        </svg>
      </div>
    ),
    diamond: (
      <div style={{ ...style, transform: `${style.transform} rotate(45deg)` }}>
        <div className="w-full h-full border-2" style={{ borderColor: color }} />
      </div>
    ),
    cross: (
      <div style={style}>
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <line x1="50" y1="10" x2="50" y2="90" stroke={color} strokeWidth="2" />
          <line x1="10" y1="50" x2="90" y2="50" stroke={color} strokeWidth="2" />
        </svg>
      </div>
    ),
    dot: (
      <div style={style}>
        <div className="w-full h-full rounded-full" style={{ backgroundColor: color }} />
      </div>
    ),
    arc: (
      <div style={style}>
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <path 
            d="M 20 50 Q 50 20 80 50" 
            fill="none" 
            stroke={color} 
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
      </div>
    ),
  };

  return shapes[type] || null;
};

// ==================== REVEAL TEXT ====================
const RevealText = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={ref} className={`overflow-visible ${className}`}>
      <div
        className="pb-2"
        style={{
          transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
          opacity: isVisible ? 1 : 0,
          transition: 'transform 0.8s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.8s ease',
        }}
      >
        {children}
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const ShiftPage = () => {
  const { isDarkMode } = useTheme();
  const { scrollY, smoothScrollY, velocity } = useSmoothScroll();
  const mouse = useMouseTracker();
  const [windowHeight, setWindowHeight] = useState(0);
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    setWindowHeight(window.innerHeight);
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track active section
  useEffect(() => {
    const sections = document.querySelectorAll('.shift-section');
    const handleScroll = () => {
      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.5 && rect.bottom > window.innerHeight * 0.5) {
          setActiveSection(index);
        }
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollProgress = Math.min(scrollY / (document.documentElement.scrollHeight - windowHeight || 1), 1);

  const shiftPanels = [
    {
      number: '01',
      title: 'The Moment',
      subtitle: 'Every so often, the way work gets done shifts completely.',
      content: "Fire. Electricity. The Internet. AI is one of those moments. Access to this power is now universal. Knowing how to use it well isn't.",
      accent: '#F6CD17',
    },
    {
      number: '02', 
      title: 'The Problem',
      subtitle: "AI can generate endlessly. It can't decide what matters.",
      content: "Without direction, it scales noise. Without judgment, it multiplies the wrong work. This shift doesn't replace human thinking — it demands better thinking.",
      accent: '#35392F',
    },
    {
      number: '03',
      title: 'How We Work',
      subtitle: 'At Flinch, strategy stays human.',
      content: "People decide what deserves focus, what fits the business, what's right for the brand. Modern intelligence accelerates the work — human judgment gives it direction.",
      accent: '#F6CD17',
    },
    {
      number: '04',
      title: 'What This Means',
      subtitle: "You don't need more tools.",
      content: "You need clarity — backed by modern capability. That's how progress happens now. The shift is here. Clear human thinking makes it work.",
      accent: '#35392F',
    }
  ];

  const methods = [
    { number: '01', title: 'Detailed Discovery', description: 'We start by understanding the business, the problem, and how you think.' },
    { number: '02', title: 'Align on Direction', description: 'Human-led strategy defines priorities, positioning, and what success looks like.' },
    { number: '03', title: 'Build the System', description: 'AI-enhanced workflows speed up exploration, iteration, and scale.' },
    { number: '04', title: 'Execute with Judgment', description: 'Systems move fast. People review, refine, and decide what\'s right.' },
    { number: '05', title: 'Collaborate & Iterate', description: 'We stay close, work in the open, and adapt quickly.' },
    { number: '06', title: 'Learn & Improve', description: 'Performance informs decisions. The system sharpens over time.' },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      {/* SEO */}
      <SEO 
        title="The Shift - Marketing in the AI Era"
        description="AI changes everything—except the need for sharp thinking. Learn how FLINCH navigates the shift to deliver faster, smarter marketing outcomes."
        canonical="/shift"
      />
      
      <Header />
      <BackToTop />

      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 z-50 bg-white/5">
        <div 
          className="h-full bg-gradient-to-r from-[#F6CD17] to-[#F6CD17]/50"
          style={{ 
            width: `${scrollProgress * 100}%`,
            transition: 'width 0.1s linear',
          }}
        />
      </div>

      {/* ===== HERO SECTION ===== */}
      <section className="min-h-[120vh] relative overflow-hidden shift-section">
        {/* Interconnected Particles */}
        <InterconnectedParticles count={40} color="#F6CD17" connectDistance={120} />

        {/* Parallax Shapes - Decorative elements around the edges - Hidden on mobile */}
        <div className="absolute inset-0 pointer-events-none hidden md:block">
          <ParallaxShape type="hexagon" size={100} color="#F6CD17" x={10} y={20} scrollY={scrollY} smoothScrollY={smoothScrollY} mouse={mouse} parallaxSpeed={0.15} rotationSpeed={0.02} mouseInfluence={30} />
          <ParallaxShape type="circle" size={150} color="#35392F" x={85} y={25} scrollY={scrollY} smoothScrollY={smoothScrollY} mouse={mouse} parallaxSpeed={0.1} mouseInfluence={40} />
          <ParallaxShape type="triangle" size={70} color="#F6CD17" x={15} y={70} scrollY={scrollY} smoothScrollY={smoothScrollY} mouse={mouse} parallaxSpeed={0.2} rotationSpeed={-0.03} mouseInfluence={25} />
          <ParallaxShape type="diamond" size={60} color="#ffffff" x={80} y={65} scrollY={scrollY} smoothScrollY={smoothScrollY} mouse={mouse} parallaxSpeed={0.18} rotationSpeed={0.04} mouseInfluence={35} />
          <ParallaxShape type="dot" size={15} color="#F6CD17" x={30} y={50} scrollY={scrollY} smoothScrollY={smoothScrollY} mouse={mouse} parallaxSpeed={0.25} mouseInfluence={20} />
          <ParallaxShape type="dot" size={10} color="#35392F" x={70} y={45} scrollY={scrollY} smoothScrollY={smoothScrollY} mouse={mouse} parallaxSpeed={0.22} mouseInfluence={15} />
        </div>

        {/* Main Hero Content */}
        <div 
          className="relative z-10 flex flex-col items-center pt-32 pb-16 px-6"
        >
          {/* Headline section - fades with scroll */}
          <div
            style={{ 
              transform: `translateY(${smoothScrollY * 0.2}px)`,
              opacity: Math.max(1 - scrollY / 400, 0)
            }}
          >
            <MagneticElement strength={15}>
              <div className="mb-8 text-center">
                <span className="inline-block px-4 py-2 bg-white/5 border border-white/10 rounded-full text-xs uppercase tracking-widest text-white/60 backdrop-blur-sm">
                  Perspective
                </span>
              </div>
            </MagneticElement>

            <h1 className="headline-xl text-center mb-4">
              <RevealText delay={100}>
                <span 
                  className="block text-[18vw] md:text-[14vw] lg:text-[12vw] text-[#F6CD17]"
                  style={{ 
                    transform: `translateX(${(mouse.x - 0.5) * -20}px)`,
                    transition: 'transform 0.3s ease-out',
                  }}
                >
                  Shift:
                </span>
              </RevealText>
              <RevealText delay={200}>
                <span 
                  className="block text-[8vw] md:text-[6vw] lg:text-[5vw] text-white"
                  style={{ 
                    transform: `translateX(${(mouse.x - 0.5) * 30}px)`,
                    transition: 'transform 0.3s ease-out',
                  }}
                >
                  Marketing in the Age of AI
                </span>
              </RevealText>
            </h1>
          </div>

          {/* ===== REDESIGNED SUBTITLE SECTION ===== */}
          <div className="mt-8 md:mt-16 w-full max-w-4xl mx-auto">
            {/* Animated Container with subtle glow */}
            <div 
              className="relative group"
              style={{ 
                transform: `translateY(${(mouse.y - 0.5) * 8}px)`,
                transition: 'transform 0.4s ease-out',
              }}
            >
              {/* Outer glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-[#F6CD17]/0 via-[#F6CD17]/10 to-[#F6CD17]/0 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              {/* Main Container */}
              <div className="relative bg-white/[0.02] backdrop-blur-sm border border-white/10 rounded-3xl p-10 md:p-14 text-center overflow-hidden">
                {/* Animated corner accents */}
                <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-[#F6CD17]/30 rounded-tl-3xl" />
                <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-[#F6CD17]/30 rounded-br-3xl" />
                
                {/* Subtle floating particles inside */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-1 h-1 bg-[#F6CD17]/30 rounded-full animate-pulse"
                      style={{
                        left: `${15 + i * 15}%`,
                        top: `${20 + (i % 3) * 25}%`,
                        animationDelay: `${i * 0.3}s`,
                        animationDuration: `${2 + i * 0.5}s`,
                      }}
                    />
                  ))}
                </div>
                
                {/* Content with elegant spacing */}
                <div className="relative z-10 space-y-8">
                  {/* Opening - subtle, sets context */}
                  <p className="text-sm md:text-base text-white/40 tracking-widest uppercase font-light">
                    Tools will change. Speed will increase.
                  </p>
                  
                  {/* Main statement - prominent */}
                  <h2 className="headline-xl text-3xl md:text-4xl lg:text-5xl text-white/90">
                    The work will keep evolving.
                  </h2>
                  
                  {/* Decorative line */}
                  <div className="flex items-center justify-center gap-4">
                    <div className="w-16 h-[1px] bg-gradient-to-r from-transparent to-[#F6CD17]/50" />
                    <div className="w-2 h-2 bg-[#F6CD17]/50 rotate-45" />
                    <div className="w-16 h-[1px] bg-gradient-to-l from-transparent to-[#F6CD17]/50" />
                  </div>
                  
                  {/* Core insight */}
                  <p className="text-lg md:text-xl text-white/60 leading-relaxed max-w-2xl mx-auto">
                    Advantage comes from continuously applying{' '}
                    <span className="text-[#F6CD17] font-semibold relative">
                      judgment
                      <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[#F6CD17]/50" />
                    </span>
                    {' '}— as conditions shift.
                  </p>
                  
                  {/* Closing - bold call to action */}
                  <div className="pt-4">
                    <p className="headline-xl text-2xl md:text-3xl lg:text-4xl text-white">
                      That's the shift we navigate.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* ===== SCROLL ENCOURAGEMENT - "The Shift" Animation ===== */}
            <div className="mt-16 md:mt-24 flex flex-col items-center">
              {/* Animated arrow sequence */}
              <div className="relative h-32 flex flex-col items-center justify-start">
                {/* Pulsing text */}
                <p className="text-xs uppercase tracking-widest text-white/30 mb-6 animate-pulse">
                  Discover the shift
                </p>
                
                {/* Animated chevrons */}
                <div className="flex flex-col items-center gap-1">
                  {[0, 1, 2].map((i) => (
                    <svg
                      key={i}
                      className="w-6 h-6 text-[#F6CD17]/60"
                      style={{
                        animation: `shiftBounce 1.5s ease-in-out infinite`,
                        animationDelay: `${i * 0.15}s`,
                        opacity: 1 - i * 0.25,
                      }}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Legacy Scroll Indicator - Hidden, replaced by new design */}
        <div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2 hidden"
          style={{ opacity: Math.max(1 - scrollY / 200, 0) }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/40 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* ===== CONTENT PANELS ===== */}
      {shiftPanels.map((panel, index) => (
        <section 
          key={panel.number}
          className="min-h-screen relative flex items-center px-6 md:px-12 shift-section overflow-hidden"
        >
          {/* Animated Background */}
          <div 
            className="absolute inset-0 transition-all duration-1000"
            style={{
              background: `radial-gradient(ellipse at ${index % 2 === 0 ? '70%' : '30%'} 50%, ${panel.accent}08 0%, transparent 50%)`,
              opacity: activeSection === index + 1 ? 1 : 0.3,
            }}
          />

          {/* Parallax Shapes for this section - Hidden on mobile to prevent text overlap */}
          <div className="absolute inset-0 pointer-events-none hidden md:block">
            <ParallaxShape 
              type={index % 2 === 0 ? 'ring' : 'hexagon'} 
              size={180} 
              color={panel.accent} 
              x={index % 2 === 0 ? 65 : 20} 
              y={30} 
              scrollY={scrollY} 
              smoothScrollY={smoothScrollY} 
              mouse={mouse} 
              parallaxSpeed={0.08} 
              rotationSpeed={0.015} 
              mouseInfluence={40}
              delay={index * windowHeight}
            />
            <ParallaxShape 
              type={index % 2 === 0 ? 'triangle' : 'diamond'} 
              size={80} 
              color={panel.accent} 
              x={index % 2 === 0 ? 80 : 35} 
              y={65} 
              scrollY={scrollY} 
              smoothScrollY={smoothScrollY} 
              mouse={mouse} 
              parallaxSpeed={0.12} 
              rotationSpeed={index % 2 === 0 ? -0.02 : 0.03} 
              mouseInfluence={30}
              delay={index * windowHeight}
            />
            <ParallaxShape 
              type="dot" 
              size={12} 
              color={panel.accent} 
              x={index % 2 === 0 ? 70 : 25} 
              y={50} 
              scrollY={scrollY} 
              smoothScrollY={smoothScrollY} 
              mouse={mouse} 
              parallaxSpeed={0.2} 
              mouseInfluence={20}
              delay={index * windowHeight}
            />
          </div>

          {/* Large Background Number */}
          <div 
            className="absolute inset-0 flex items-center pointer-events-none overflow-hidden"
            style={{
              justifyContent: index % 2 === 0 ? 'flex-end' : 'flex-start',
              padding: index % 2 === 0 ? '0 5% 0 0' : '0 0 0 5%',
            }}
          >
            <span 
              className="text-[30vw] md:text-[22vw] font-display font-bold leading-none select-none"
              style={{ 
                WebkitTextStroke: '1px rgba(255,255,255,0.02)',
                WebkitTextFillColor: 'transparent',
                transform: `translateY(${(smoothScrollY - index * windowHeight) * 0.05}px)`,
              }}
            >
              {panel.number}
            </span>
          </div>

          {/* Content */}
          <div className="max-w-7xl mx-auto w-full relative z-10">
            <div className={`max-w-xl ${index % 2 === 0 ? '' : 'ml-auto text-right'}`}>
              <RevealText>
                <div 
                  className={`w-16 h-1 rounded-full mb-6 ${index % 2 === 1 ? 'ml-auto' : ''}`}
                  style={{ backgroundColor: panel.accent }}
                />
              </RevealText>
              <RevealText delay={100}>
                <h2 className="font-display text-5xl md:text-6xl lg:text-7xl mb-4 leading-[1.1]">
                  {panel.title}
                </h2>
              </RevealText>
              <RevealText delay={200}>
                <p className="text-2xl md:text-3xl text-white/80 mb-4 leading-[1.4]">
                  {panel.subtitle}
                </p>
              </RevealText>
              <RevealText delay={300}>
                <p className="text-lg text-white/60 leading-relaxed">
                  {panel.content}
                </p>
              </RevealText>
            </div>
          </div>
        </section>
      ))}

      {/* ===== THE FLINCH METHOD ===== */}
      <section id="flinch-method" className="py-32 px-6 md:px-12 bg-[#0D0D0D] relative overflow-hidden shift-section">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <RevealText>
              <span className="inline-block px-4 py-2 bg-[#F6CD17]/10 border border-[#F6CD17]/20 rounded-full text-xs uppercase tracking-widest text-[#F6CD17] mb-6">
                Our Process
              </span>
            </RevealText>
            <RevealText delay={100}>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mb-6">
                The Flinch Method
              </h2>
            </RevealText>
            <RevealText delay={200}>
              <p className="text-xl text-white/40 max-w-2xl mx-auto">
                A human-led approach, amplified by modern capability.
              </p>
            </RevealText>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {methods.map((method, index) => (
              <MagneticElement key={method.number} strength={10}>
                <div
                  className="group relative bg-white/[0.02] border border-white/5 rounded-3xl p-8 cursor-pointer overflow-hidden"
                  style={{
                    transition: 'all 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                    e.currentTarget.style.borderColor = 'rgba(246,205,23,0.2)';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  {/* Background number */}
                  <span className="absolute top-4 right-4 text-7xl font-display font-bold text-white/[0.02] group-hover:text-white/[0.06] transition-all duration-500">
                    {method.number}
                  </span>
                  
                  <div className="relative z-10">
                    <div className="w-8 h-1 bg-[#F6CD17] rounded-full mb-6 group-hover:w-14 transition-all duration-500" />
                    <h3 className="font-display text-2xl mb-4 group-hover:text-[#F6CD17] transition-colors duration-300">
                      {method.title}
                    </h3>
                    <p className="text-white/40 leading-relaxed group-hover:text-white/60 transition-colors duration-300">
                      {method.description}
                    </p>
                  </div>

                  {/* Hover gradient */}
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                      background: 'radial-gradient(circle at 50% 0%, rgba(246,205,23,0.05) 0%, transparent 70%)',
                    }}
                  />
                </div>
              </MagneticElement>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CLOSING CTA ===== */}
      <section className="py-40 md:py-56 px-6 relative overflow-hidden shift-section">
        {/* Background Shapes */}
        <div className="absolute inset-0 pointer-events-none">
          <ParallaxShape type="ring" size={350} color="#F6CD17" x={15} y={30} scrollY={scrollY} smoothScrollY={smoothScrollY} mouse={mouse} parallaxSpeed={0.03} rotationSpeed={0.008} mouseInfluence={60} />
          <ParallaxShape type="hexagon" size={200} color="#35392F" x={80} y={60} scrollY={scrollY} smoothScrollY={smoothScrollY} mouse={mouse} parallaxSpeed={0.04} rotationSpeed={-0.01} mouseInfluence={50} />
        </div>

        {/* Background Text */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <span 
            className="text-[18vw] font-display font-bold text-white/[0.01] whitespace-nowrap"
            style={{ transform: `translateX(${(smoothScrollY - windowHeight * 5) * 0.05}px)` }}
          >
            SHIFT
          </span>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <RevealText>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mb-8">
              The shift is here.
            </h2>
          </RevealText>
          <RevealText delay={150}>
            <p className="text-xl md:text-2xl text-white/40 mb-16">
              Clear human thinking makes it work.
            </p>
          </RevealText>
          <RevealText delay={300}>
            <MagneticElement strength={20}>
              <Link 
                to="/"
                onClick={(e) => {
                  e.preventDefault();
                  // Open contact modal
                  const btn = document.querySelector('header button');
                  if (btn) btn.click();
                }}
                className="inline-flex items-center gap-3 px-10 py-5 bg-[#F6CD17] text-black rounded-full text-lg font-medium hover:shadow-[0_0_60px_rgba(246,205,23,0.4)] transition-all duration-500 group"
              >
                Navigate the shift with us
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 16 16" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="1.5"
                  className="transition-transform duration-300 group-hover:translate-x-1"
                >
                  <path d="M3 8H13M13 8L9 4M13 8L9 12" />
                </svg>
              </Link>
            </MagneticElement>
          </RevealText>
        </div>
      </section>

      {/* Navigation Dots */}
      <div className="fixed right-6 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col gap-3">
        {['Hero', ...shiftPanels.map(p => p.number), 'For You', 'Method', 'CTA'].map((label, index) => (
          <button
            key={index}
            className="relative group"
            onClick={() => {
              const sections = document.querySelectorAll('.shift-section');
              sections[index]?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <div 
              className={`w-2 h-2 rounded-full transition-all duration-500 ${
                activeSection === index 
                  ? 'bg-[#F6CD17] scale-150' 
                  : 'bg-white/20 hover:bg-white/40 hover:scale-125'
              }`}
            />
            <span className="absolute right-5 top-1/2 -translate-y-1/2 text-[10px] text-white/0 group-hover:text-white/40 transition-all duration-300 whitespace-nowrap font-mono">
              {label}
            </span>
          </button>
        ))}
      </div>

      <Footer />
    </div>
  );
};

export default ShiftPage;
